import React, { useState, useMemo } from 'react';
import {
  Card,
  ScorePill,
  Delta,
  TH,
  TD,
  TD_BASE,
  TD_WRAP,
  Empty,
  HowCalculated,
  DefinitionTable,
  fmtMins,
  ago,
} from '../MetricsUI';

/**
 * One row per person.
 *
 * COLOUR MEANS ONE THING HERE: performance. Score pills and improvement arrows
 * are coloured; nothing else is.
 *
 * The earlier version also had a coloured status chip — Power / Active / Dormant
 * — which put two unrelated colour scales in one table, so a green chip and a
 * green score looked related and were not. Engagement is now stated in words and
 * numbers instead: "last seen" as plain text, and active days as a plain
 * fraction. That also gets "power user" out of a column it never belonged in —
 * it is a frequency band, not a status, and it still appears in Adoption where
 * there is no score colour to collide with.
 */

const ROLE_LABEL = { trainee: 'Trainee', leader: 'Manager', admin: 'Admin' };

/** "today" / "3mo ago" / "never" — no colour, deliberately. */
const lastSeen = (m) => (m.everStarted ? ago(m.lifetime.lastActiveAt) : 'never');

const csvEscape = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Short region labels for the column. */
const REGION_LABEL = { us: 'US', eu: 'EU', ap: 'APAC' };

export default function PeopleTable({ members, anonymous, onOpenLearner, orgName = 'organisation' }) {
  const [sort, setSort] = useState({ key: 'improvement', dir: 'desc' });

  /*
   * The column only appears when the roster actually spans regions. A
   * single-region organisation — which is most of them — should not pay a
   * column of identical badges for a distinction that does not apply to it.
   */
  const multiRegion = useMemo(() => {
    const seen = new Set();
    (members || []).forEach((m) => (m.regions || []).forEach((r) => seen.add(r)));
    return seen.size > 1;
  }, [members]);

  const rows = useMemo(() => {
    const val = (m) => {
      switch (sort.key) {
        case 'displayLabel':
          return m.displayLabel || '';
        case 'region':
          // Sorted on the region list, so somebody in us+eu groups with us.
          return (m.regions || []).join('+');
        case 'role':
          return m.role || '';
        case 'level':
          return m.level || '';
        case 'lastSeen':
          return m.lifetime.lastActiveAt;
        case 'activeDays':
          return m.lifetime.activeDaysLast30;
        case 'chapters':
          return m.lifetime.chapterPercent;
        case 'time':
          return m.window.totalMs;
        case 'cases':
          return m.lifetime.caseStudiesAttempted;
        case 'avatars':
          return m.lifetime.avatarsFaced;
        case 'score':
          return m.lifetime.avgLatestScore;
        case 'improvement':
          return m.lifetime.avgImprovement;
        case 'quiz':
          return m.lifetime.avgQuizScore;
        default:
          return 0;
      }
    };
    return [...members].sort((a, b) => {
      const x = val(a);
      const y = val(b);
      // Nulls last in either direction: "not measurable" is not a low score.
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      const cmp = typeof x === 'string' ? x.localeCompare(y) : x - y;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [members, sort]);

  const download = () => {
    const header = [
      anonymous ? 'Employee number' : 'Employee',
      ...(anonymous ? [] : ['Email']),
      'Role',
      'Level',
      'Last seen',
      'Active days (last 30)',
      'Chapters complete',
      'Chapters in scope',
      'Chapter %',
      'Time in range (minutes)',
      'Case studies attempted',
      'Case studies in scope',
      'Avatars faced',
      'Avg latest score %',
      'Improvement (pts)',
      'Quiz avg %',
    ];
    const body = rows.map((m) =>
      [
        m.displayLabel,
        ...(anonymous ? [] : [m.email || '']),
        ROLE_LABEL[m.role] || m.role,
        m.level || '',
        lastSeen(m),
        m.lifetime.activeDaysLast30,
        m.lifetime.chaptersComplete,
        m.lifetime.chaptersInScope,
        m.lifetime.chapterPercent,
        Math.round(m.window.totalMs / 60000),
        m.lifetime.caseStudiesAttempted,
        m.lifetime.caseStudiesInScope,
        m.lifetime.avatarsFaced,
        m.lifetime.avgLatestScore == null ? '' : Math.round(m.lifetime.avgLatestScore),
        m.lifetime.avgImprovement == null ? '' : Math.round(m.lifetime.avgImprovement),
        m.lifetime.avgQuizScore == null ? '' : Math.round(m.lifetime.avgQuizScore),
      ].map(csvEscape)
    );

    const csv = [header.map(csvEscape).join(','), ...body.map((r) => r.join(','))].join('\n');
    // BOM so Excel opens it as UTF-8 rather than mangling any accented name.
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${orgName.replace(/[^\w-]+/g, '-').toLowerCase()}-people-${
      new Date().toISOString().slice(0, 10)
    }.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const th = (key, label, extra = '') => (
    <th
      onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }))}
      className={`cursor-pointer select-none hover:text-navy ${TH} ${extra}`}
      title="Click to sort; click again to reverse"
    >
      {label}
      {sort.key === key && <span className="ml-1 text-lavender">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );

  return (
    <Card className="overflow-hidden p-0">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <p className="text-sm font-semibold text-navy">
          {members.length} {members.length === 1 ? 'employee' : 'employees'}
          <HowCalculated wide label="Reading this table">
            <DefinitionTable
              rows={[
                ['Colour', 'Only on score and improvement — nothing else is coloured'],
                ['Last seen', 'Time since their most recent recorded module'],
                ['Active days', 'Days with any activity in the last 30'],
                ['Chapters', 'Complete, out of those in their own learning path'],
                ['Time', 'In the selected date range; everything else is lifetime'],
                ['Avatars', 'Distinct AI personalities faced, of five'],
              ]}
            />
          </HowCalculated>
        </p>
        <button
          onClick={download}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:border-lavender hover:text-navy"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 2a1 1 0 011 1v8.6l2.3-2.3a1 1 0 011.4 1.4l-4 4a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L9 11.6V3a1 1 0 011-1z" />
            <path d="M3 15a1 1 0 011 1v1h12v-1a1 1 0 112 0v1a2 2 0 01-2 2H4a2 2 0 01-2-2v-1a1 1 0 011-1z" />
          </svg>
          Download
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[1180px]">
          <thead className="bg-gray-50">
            <tr>
              {th('displayLabel', anonymous ? 'Employee (anonymised)' : 'Employee')}
              {multiRegion && th('region', 'Region', 'w-20')}
              {th('role', 'Role', 'w-24')}
              {th('level', 'Level', 'w-20')}
              {th('lastSeen', 'Last seen', 'w-28')}
              {th('activeDays', 'Active days', 'w-28')}
              {th('chapters', 'Chapters', 'w-40')}
              {th('time', 'Time', 'w-24')}
              {th('cases', 'Cases', 'w-20')}
              {th('avatars', 'Avatars', 'w-24')}
              {th('score', 'Latest', 'w-24')}
              {th('improvement', 'Improvement', 'w-32')}
              {th('quiz', 'Quiz avg', 'w-24')}
              <th className="w-8" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((m) => (
              <tr
                key={m.userId}
                onClick={() => onOpenLearner(m.userId, m.displayLabel, (m.userIds || []).map((u) => u.userId))}
                className="cursor-pointer hover:bg-indigo-50/50"
              >
                <td className={TD_WRAP}>
                  <p className="text-sm font-semibold text-navy">{m.displayLabel}</p>
                  {!m.anonymous && m.email && <p className="text-xs text-gray-400">{m.email}</p>}
                </td>
                {/* Which region a person's record lives in, and whether that
                    region reports names. Without it a part-anonymous roster
                    looks like half the names are simply missing. */}
                {multiRegion && (
                  <td className={`${TD_BASE} text-gray-500`}>
                    {/* One chip per region. A person with a record in more than
                        one region is ONE row now, so the cell has to show every
                        region their progress was gathered from. */}
                    {(m.regions || []).length ? (
                      <span className="inline-flex flex-wrap gap-1">
                        {m.regions.map((r) => (
                          <span
                            key={r}
                            className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] uppercase tracking-wide"
                          >
                            {REGION_LABEL[r] || r}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                    {m.anonymous && <span className="ml-1 text-[11px] text-gray-400">anon</span>}
                  </td>
                )}
                <td className={`${TD_BASE} text-gray-500`}>{ROLE_LABEL[m.role] || m.role}</td>
                <td className={`${TD_BASE} text-gray-500`}>
                  {m.level ? m.level.replace(/^Level\s*/i, 'L') : '—'}
                </td>
                <td className={`${TD_BASE} text-gray-600`}>
                  {m.everStarted ? lastSeen(m) : <span className="italic text-gray-400">never</span>}
                </td>
                <td className={`${TD_BASE} tabular-nums text-gray-600`}>
                  {m.lifetime.activeDaysLast30}
                  <span className="text-gray-400"> / 30</span>
                </td>
                <td className={TD}>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-lavender"
                        style={{ width: `${m.lifetime.chapterPercent}%` }}
                      />
                    </div>
                    <span className="text-xs tabular-nums text-gray-500">
                      {m.lifetime.chaptersComplete}/{m.lifetime.chaptersInScope}
                    </span>
                  </div>
                </td>
                <td className={`${TD_BASE} text-gray-600`}>
                  {m.window.totalMs ? fmtMins(m.window.totalMs) : '—'}
                </td>
                <td className={`${TD_BASE} tabular-nums text-gray-600`}>
                  {m.lifetime.caseStudiesAttempted}
                </td>
                <td className={`${TD_BASE} tabular-nums text-gray-600`}>
                  {m.lifetime.avatarsTotal
                    ? `${m.lifetime.avatarsFaced}/${m.lifetime.avatarsTotal}`
                    : m.lifetime.avatarsFaced}
                </td>
                <td className={TD}>
                  <ScorePill pct={m.lifetime.avgLatestScore} />
                </td>
                <td className={TD}>
                  <Delta value={m.lifetime.avgImprovement} />
                  {m.lifetime.measurableModules > 0 && (
                    <span className="ml-1 text-[10px] text-gray-400">n={m.lifetime.measurableModules}</span>
                  )}
                </td>
                <td className={`${TD_BASE} tabular-nums text-gray-600`}>
                  {m.lifetime.avgQuizScore == null ? '—' : `${Math.round(m.lifetime.avgQuizScore)}%`}
                </td>
                <td className={`${TD_BASE} text-gray-300`}>›</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={13}>
                  <Empty>Nobody matches these filters.</Empty>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

    </Card>
  );
}
